import { addError, addSuccess } from '@/store/reducers/ToastStore';
import { FeedbackPopover } from '@/components/chat/FeedbackPopover';
import { getChatAPI } from '@/api/ChatApi';
import { getLayoverLogger } from '@/logging/LayoverLogger';
import {
  mkTextFeedbackBtnTrackAttrs,
  THREAD_FEEDBACK_PREFIX,
} from '@/analytics/props/feedback';
import { optAppConfig } from '@/utils/config';
import { TABLE_ANNOTATION } from 'configs/NoraVariants';
import { Thread } from '@/models/Thread';
import { useAppDispatch } from '@/store/hooks';

import { Fragment, MouseEventHandler, ReactElement, useState } from 'react';
import styled from 'styled-components';

const noraExperienceVariant = optAppConfig((_) => _.experience.variant);

type ThreadFeedbackProps = {
  thread: Thread;
};

const threadFeedbackTemplateText =
  noraExperienceVariant === TABLE_ANNOTATION
    ? 'I was trying to ...\n\n[GOOD/BAD]Here is what happened: ...'
    : undefined;

const threadFeedbackInstructionsText =
  noraExperienceVariant === TABLE_ANNOTATION
    ? 'In the first line, please describe what you were trying to achieve.\nIn the second line, please describe how successful you were. If you were successful, start with "[GOOD]", if you were unsucessful or neutral, please start with "[BAD]"'
    : undefined;

export const ThreadFeedback = ({
  thread,
}: ThreadFeedbackProps): ReactElement => {
  const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false);
  const [feedbackFormAnchorEl, setFeedbackFormAnchorEl] =
    useState<Element | null>(null);

  const dispatch = useAppDispatch();
  const chatApi = getChatAPI();

  const onFeedbackClick: MouseEventHandler = (event) => {
    if (isFeedbackFormOpen) {
      setIsFeedbackFormOpen(false);
    } else {
      setFeedbackFormAnchorEl(event.currentTarget);
      setIsFeedbackFormOpen(true);
    }
  };

  const onCloseFeedbackForm = () => {
    setIsFeedbackFormOpen(false);
  };

  const onSubmitFeedback = async (feedback: string) => {
    try {
      await chatApi.saveThreadFeedback({
        threadId: thread.id,
        feedback,
      });
      dispatch(addSuccess({ text: 'Thanks for your feedback!' }));
    } catch (e: any) {
      getLayoverLogger().logError('Error saving thread feedback:', e);
      dispatch(
        addError({
          text: 'Error submitting feedback. Please try again.',
        }),
      );
      // The error is caught here to be able to log specific issue
      // but thrown, because UI error handling is done in `FeedbackPopover.tsx`
      throw e;
    }
  };

  return (
    <Fragment>
      <FeedbackButton
        onClick={onFeedbackClick}
        data-testid="open-thread-feedback"
        {...mkTextFeedbackBtnTrackAttrs(THREAD_FEEDBACK_PREFIX, {
          threadId: thread.id,
        })}
      >
        {noraExperienceVariant !== TABLE_ANNOTATION ? (
          <span>Leave Thread Feedback</span>
        ) : (
          <span>Click Here</span>
        )}
      </FeedbackButton>
      <FeedbackPopover
        isFeedbackFormOpen={isFeedbackFormOpen}
        closeFeedbackForm={onCloseFeedbackForm}
        feedbackFormAnchorEl={feedbackFormAnchorEl}
        onSubmitFeedback={onSubmitFeedback}
        messageTemplateText={threadFeedbackTemplateText}
        instructionsText={threadFeedbackInstructionsText}
        heapPrefix={THREAD_FEEDBACK_PREFIX}
        heapProps={{ threadId: thread.id }}
      />
    </Fragment>
  );
};

const FeedbackButton = styled.button`
  color: #57dbaf;
  background: none;
  border: none;
  cursor: pointer;
  padding-left: 4px;
  transition: opacity 250ms ease-in-out;

  &:hover {
    opacity: 0.7;
    transition-duration: 75ms;
  }
`;
